import {AudioTrack, AudioTrackList, TextTrack, TextTrackList} from "@vidstack/react";
import {getLanguageInfoByCode, getLanguageSwitcherDisplayTextTranslation} from "@/lib/LanguageUtils";

/**
 * LanguageTrack is a data structure that represents a language track in the player.
 * It is a combination of an AudioTrack and a TextTrack for a particular language.
 */
export interface LanguageTrack {
    key: string,
    languageCode: string,
    countryCode: string | null,
    label: string,
    nativeLabel: string,
    audioTrack?: AudioTrack,
    textTrack?: TextTrack,
    default?: boolean
}

/**
 *
 */
export type LanguageTrackList = LanguageTrack[];

/**
 * Get language codes from audio and text tracks and order them based on the languageOrder array.
 * @param languageOrder
 * @param audioTracks
 * @param textTracks
 * @returns
 */
export function getLanguageTracks(languageOrder: string[], audioTracks?: AudioTrackList, textTracks?: TextTrackList): LanguageTrackList {
    const languages: LanguageTrackList = [];
    //Populate the languages array with the language codes from audioTracks first
    //Then populate the languages array with the language codes from textTracks

    if (!audioTracks && textTracks) return languages;

    if (audioTracks) {
        for (let i = 0; i < audioTracks.length; i++) {
            const audioTrack = audioTracks[i];
            if (audioTrack) {
                languages.push({
                    key: audioTrack.language + audioTrack.label,
                    languageCode: audioTrack.language,
                    countryCode: getLanguageInfoByCode(audioTrack.language)?.countryCode || null,
                    label: audioTrack.label,
                    nativeLabel: audioTrack.label,
                    audioTrack: audioTrack,
                });
            }
        }
        if (textTracks) {
            for (let i = 0; i < textTracks.length; i++) {
                const textTrack = textTracks[i];
                if (textTrack) {
                    const langEntry = languages.find((lang) => lang.languageCode === textTrack.language);
                    if (langEntry) {
                        langEntry.textTrack = textTrack;
                    } else {
                        languages.push({
                            key: textTrack.language + textTrack.label,
                            languageCode: textTrack.language,
                            countryCode: getLanguageInfoByCode(textTrack.language)?.countryCode || null,
                            label: textTrack.label,
                            nativeLabel: textTrack.label,
                            textTrack: textTrack,
                        });
                    }
                }
            }
        }
    }

    //Order list of language based on the languageOrder array
    const orderedLanguages: LanguageTrackList = [];
    languageOrder.forEach((langCode) => {
        const langEntry = languages.find((lang) => lang.languageCode === langCode);
        if (langEntry) {
            orderedLanguages.push(langEntry);
        }
    });
    //If there are any items in language that are not in language order then they should be placed at the end of the ordered list
    languages.forEach((lang) => {
        if (!orderedLanguages.find((orderedLang) => orderedLang.languageCode === lang.languageCode)) {
            orderedLanguages.push(lang);
        }
    });

    return orderedLanguages;
}

/**
 * Set the active audio track based on the target language.
 * @param languageTracks
 * @param targetLanguage
 */
export function setActiveAudioTrack(languageTracks: LanguageTrackList, targetLanguage: string) {
    // Select the audio track that matches the target language, but fall back to the default language if the target language is not available.
    let audioSwitched = false;

    for (const track of languageTracks) {
        if (!track.audioTrack) {
            continue;
        }
        if (track.audioTrack.language === targetLanguage || !track.audioTrack.language && targetLanguage === 'en') {
            track.audioTrack.selected = true
            audioSwitched = true;
            console.log('Audio track selected -', track.audioTrack.language, targetLanguage)
        }
    }
    //If the language could not be found then switch to the default language.
    if (!audioSwitched) {
        // @ts-ignore
        for (const track of languageTracks) {
            if (!track.audioTrack) {
                continue;
            }
            if (track && !track.audioTrack.language) {
                console.log('Language not available, using default audio track', targetLanguage)
                track.audioTrack.selected = true;
                audioSwitched = true;
                return
            }
        }
    }
}

/**
 * Set the active text track based on the target language.
 * @param languageTracks
 * @param targetLanguage
 * @param showCaptions
 */
export function setActiveTextTrack(languageTracks: LanguageTrackList, targetLanguage: string, showCaptions: boolean) {
    // showTextTrack boolean is used to hide text track so player doesn't auto show captions when its first loaded
    console.debug('Setting text track for language:', targetLanguage, 'showCaptions:', showCaptions, 'languageTracks:', languageTracks)

    let textTrackSwitched = false;
    //@ts-ignore
    for (const track of languageTracks) {
        if (!track.textTrack) {
            continue;
        }
        console.log('Text track evaluating -', track.textTrack)
        if (track.textTrack.language === targetLanguage && !textTrackSwitched) {
            console.log('Text track showing -', track.textTrack.language, targetLanguage)
            track.textTrack.mode = showCaptions ? 'showing' : 'hidden';
            //Once a text track is selected we want to hide all other text tracks even if the language matches
            textTrackSwitched = true;
        } else {
            track.textTrack.mode = "disabled";
        }
        console.log('Text track selected -', track.textTrack.language, targetLanguage, track.textTrack.mode)

    }
}

/**
 * Get the preferred languages from the browser.
 */
export function getPreferredLanguages(): string[] {
    const languages: string[] = [];
    const browserLanguages = navigator.languages;
    console.log('Browser languages:', browserLanguages)
    for (let i = 0; i < browserLanguages.length; i++) {
        const lang = browserLanguages[i];
        languages.push(lang);
    }
    return languages;
}

export function getPreferredLanguageTrack(languages: LanguageTrackList): LanguageTrack | undefined {
    const browserLanguages = getPreferredLanguages();
    for (let i = 0; i < browserLanguages.length; i++) {
        const langWithRegion = browserLanguages[i]; // This format looks like "en-US"
        const langCode = langWithRegion.split('-')[0]; // This format looks like "en"

        // Try to find the language with the region code first ie. "en-US"
        for (let j = 0; j < languages.length; j++) {
            if (languages[j].languageCode === langWithRegion) {
                return languages[j];
            }
        }
        // If the language with the region code is not found then try to find
        // the language without the region code ie. "en"
        for (let j = 0; j < languages.length; j++) {
            if (languages[j].languageCode === langCode) {
                return languages[j];
            }
        }
    }
    return undefined;
}

/**
 * Get the display text for the language switcher based on the language code.
 * @returns {string} The display text for the language switcher.
 */
export function getChooseLanguageText(): string {
    const preferredLanguages = getPreferredLanguages();
    return preferredLanguages.map(lang => getLanguageSwitcherDisplayTextTranslation(lang)).find(text => text) || getLanguageSwitcherDisplayTextTranslation('en')

}
